import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Globe, Check, Loader2, Edit2, X } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Common IANA timezones grouped by region (most-used worldwide)
const COMMON_TIMEZONES = [
  { region: 'Americas', zones: [
    'America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York',
    'America/Mexico_City', 'America/Toronto', 'America/Sao_Paulo', 'America/Buenos_Aires',
  ]},
  { region: 'Europe', zones: [
    'Europe/London', 'Europe/Lisbon', 'Europe/Madrid', 'Europe/Paris', 'Europe/Berlin',
    'Europe/Amsterdam', 'Europe/Rome', 'Europe/Zurich', 'Europe/Vienna', 'Europe/Budapest',
    'Europe/Athens', 'Europe/Helsinki', 'Europe/Stockholm', 'Europe/Warsaw',
    'Europe/Bucharest', 'Europe/Moscow', 'Europe/Istanbul',
  ]},
  { region: 'Africa & Middle East', zones: [
    'Africa/Cairo', 'Africa/Johannesburg', 'Africa/Lagos', 'Africa/Nairobi',
    'Asia/Jerusalem', 'Asia/Dubai', 'Asia/Riyadh', 'Asia/Tehran',
  ]},
  { region: 'Asia & Pacific', zones: [
    'Asia/Karachi', 'Asia/Kolkata', 'Asia/Bangkok', 'Asia/Singapore', 'Asia/Jakarta',
    'Asia/Hong_Kong', 'Asia/Shanghai', 'Asia/Tokyo', 'Asia/Seoul', 'Asia/Manila',
    'Australia/Perth', 'Australia/Sydney', 'Pacific/Auckland', 'Pacific/Honolulu',
  ]},
  { region: 'Other', zones: ['UTC'] },
];

const formatOffset = (tz) => {
  try {
    const now = new Date();
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    });
    const parts = fmt.formatToParts(now);
    const offset = parts.find(p => p.type === 'timeZoneName')?.value || '';
    return offset;
  } catch {
    return '';
  }
};

const formatLocalTime = (tz) => {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hour: 'numeric', minute: '2-digit', hour12: true
    }).format(new Date());
  } catch { return ''; }
};

export default function TimezoneSetting() {
  const { user, getAuthHeader } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedTz, setSelectedTz] = useState('');
  const [now, setNow] = useState(Date.now());

  const detectedTz = (() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return ''; }
  })();
  const currentTz = user?.timezone || detectedTz || 'UTC';
  const isAutoSynced = user?.timezone === detectedTz;

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const save = async (tz) => {
    setSaving(true);
    try {
      await axios.post(`${API}/auth/timezone`, { timezone: tz }, { headers: getAuthHeader() });
      toast.success(`Timezone updated to ${tz}`);
      setEditing(false);
      // Soft refresh user to reflect new value
      setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      toast.error('Failed to update timezone');
    } finally {
      setSaving(false);
    }
  };

  const resetToDetected = () => {
    if (detectedTz && detectedTz !== currentTz) {
      save(detectedTz);
    } else {
      toast.info('Already matches your browser timezone');
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-border p-6 mb-6" data-testid="timezone-setting">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
          <Globe className="w-5 h-5 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-semibold text-lg">Timezone</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Used to fire your reminders at the correct local time.
          </p>
        </div>
      </div>

      {!editing ? (
        <>
          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg" key={now}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-[#1A1A1A]" data-testid="current-timezone">
                  {currentTz}
                </span>
                <span className="text-xs text-muted-foreground">{formatOffset(currentTz)}</span>
                {isAutoSynced && (
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-green-100 text-green-700 flex items-center gap-0.5">
                    <Check className="w-2.5 h-2.5" /> Auto-synced
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Local time: <span className="font-medium text-foreground">{formatLocalTime(currentTz)}</span>
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setSelectedTz(currentTz); setEditing(true); }}
              data-testid="edit-timezone-btn"
            >
              <Edit2 className="w-3.5 h-3.5 mr-1" />
              Edit
            </Button>
          </div>

          {detectedTz && detectedTz !== currentTz && (
            <button
              onClick={resetToDetected}
              className="mt-3 text-xs text-primary hover:underline flex items-center gap-1"
              data-testid="reset-to-detected-tz-btn"
            >
              Your browser detected: <span className="font-medium">{detectedTz}</span> — switch to it?
            </button>
          )}
        </>
      ) : (
        <div className="space-y-3" data-testid="timezone-edit-panel">
          <select
            value={selectedTz}
            onChange={(e) => setSelectedTz(e.target.value)}
            className="w-full bg-white border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            data-testid="timezone-select"
          >
            {COMMON_TIMEZONES.map(group => (
              <optgroup key={group.region} label={group.region}>
                {group.zones.map(tz => (
                  <option key={tz} value={tz}>{tz} ({formatOffset(tz)})</option>
                ))}
              </optgroup>
            ))}
          </select>
          {selectedTz && (
            <p className="text-xs text-muted-foreground">
              Local time would be <span className="font-medium text-foreground">{formatLocalTime(selectedTz)}</span>
            </p>
          )}
          <div className="flex gap-2">
            <Button
              onClick={() => save(selectedTz)}
              disabled={saving || !selectedTz}
              className="btn-primary flex-1"
              data-testid="save-timezone-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Save
            </Button>
            <Button variant="outline" onClick={() => setEditing(false)} disabled={saving}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
